const BASE_MULTIPLIERS: number[] = [1, 2, 3, 4, 5, 7, 8, 10, 12, 15, 20, 25, 30, 35, 40]

const JACKPOT_MULTIPLIERS = {
    mini: 50,
    minor: 100,
    major: 250,
    grand: 1000,
}

const COIN_MULTIPLIER_SET = new Set<number>([
    ...BASE_MULTIPLIERS,
    JACKPOT_MULTIPLIERS.mini,
    JACKPOT_MULTIPLIERS.minor,
    JACKPOT_MULTIPLIERS.major,
    JACKPOT_MULTIPLIERS.grand,
])

export const HOLD_AND_WIN_ALLOWED_MULTIPLIERS: number[] = Array.from(COIN_MULTIPLIER_SET).sort((a, b) => a - b)

// Configured credits used for coin values during reel run and after reel drop.
export const HOLD_AND_WIN_RANDOM_SPIN_MULTIPLIERS: number[] = [...HOLD_AND_WIN_ALLOWED_MULTIPLIERS]

export const HOLD_AND_WIN_MIN_MULTIPLIER: number = 1
export const HOLD_AND_WIN_MAX_MULTIPLIER: number = 1000

export interface HoldAndWinSpinCoin {
    coinType: string
    multiplier: number
}

/**
 * Jackpot coins are landable during Hold & Win, so the reels should show one going past now and
 * then. The chance is per rendered symbol and deliberately low: with the whole board rolling it
 * works out to roughly one glimpse per respin, which teases without implying a near miss.
 */
const JACKPOT_SPIN_TEASE_CHANCE = 0.025

/** Weighted so the top tiers stay markedly rarer than Mini. */
const JACKPOT_SPIN_TEASE_TIERS: ReadonlyArray<{ coinType: string; weight: number }> = [
    { coinType: 'mini', weight: 8 },
    { coinType: 'minor', weight: 4 },
    { coinType: 'major', weight: 2 },
    { coinType: 'grand', weight: 1 },
]

const JACKPOT_SPIN_TEASE_TOTAL_WEIGHT = JACKPOT_SPIN_TEASE_TIERS.reduce((total, tier) => total + tier.weight, 0)

const pickJackpotTeaseCoinType = (): string => {
    let roll = Math.random() * JACKPOT_SPIN_TEASE_TOTAL_WEIGHT

    for (const tier of JACKPOT_SPIN_TEASE_TIERS) {
        roll -= tier.weight
        if (roll <= 0) {
            return tier.coinType
        }
    }

    return JACKPOT_SPIN_TEASE_TIERS[0].coinType
}

/** Picks the coin shown for a single frame of a rolling Hold & Win cell. */
export const pickHoldAndWinSpinCoin = (): HoldAndWinSpinCoin => {
    if (Math.random() < JACKPOT_SPIN_TEASE_CHANCE) {
        const coinType = pickJackpotTeaseCoinType()

        return {
            coinType,
            multiplier: resolveJackpotMultiplier(coinType) ?? JACKPOT_MULTIPLIERS.mini,
        }
    }

    return {
        coinType: 'prize',
        multiplier:
            HOLD_AND_WIN_RANDOM_SPIN_MULTIPLIERS[
                Math.floor(Math.random() * HOLD_AND_WIN_RANDOM_SPIN_MULTIPLIERS.length)
            ],
    }
}

const toFinitePositiveNumber = (value: unknown): number | null => {
    const parsed = Number(value)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

const resolveJackpotMultiplier = (coinType: unknown): number | null => {
    const normalizedCoinType = String(coinType || '').toLowerCase()

    if (normalizedCoinType.includes('grand')) {
        return JACKPOT_MULTIPLIERS.grand
    }

    if (normalizedCoinType.includes('major')) {
        return JACKPOT_MULTIPLIERS.major
    }

    if (normalizedCoinType.includes('minor')) {
        return JACKPOT_MULTIPLIERS.minor
    }

    if (normalizedCoinType.includes('mini')) {
        return JACKPOT_MULTIPLIERS.mini
    }

    return null
}

const nearestAllowedMultiplier = (value: number): number => {
    let nearest = HOLD_AND_WIN_ALLOWED_MULTIPLIERS[0]
    let nearestDistance = Math.abs(value - nearest)

    for (let index = 1; index < HOLD_AND_WIN_ALLOWED_MULTIPLIERS.length; index++) {
        const candidate = HOLD_AND_WIN_ALLOWED_MULTIPLIERS[index]
        const distance = Math.abs(value - candidate)
        if (distance < nearestDistance) {
            nearest = candidate
            nearestDistance = distance
        }
    }

    return nearest
}

const pickBestCandidate = (candidates: number[]): number => {
    let bestCandidate = candidates[0]
    let bestDistance = Math.abs(bestCandidate - nearestAllowedMultiplier(bestCandidate))

    for (let index = 1; index < candidates.length; index++) {
        const candidate = candidates[index]
        const distance = Math.abs(candidate - nearestAllowedMultiplier(candidate))
        if (distance < bestDistance) {
            bestCandidate = candidate
            bestDistance = distance
        }
    }

    return bestCandidate
}

export const normalizeHoldAndWinCoinValue = (
    rawCoinValue: unknown,
    coinType?: unknown,
    spinCoinValue?: unknown
): number | undefined => {
    const jackpotMultiplier = resolveJackpotMultiplier(coinType)
    if (jackpotMultiplier !== null) {
        return jackpotMultiplier
    }

    const parsedCoinValue = toFinitePositiveNumber(rawCoinValue)
    if (parsedCoinValue === null) {
        return undefined
    }

    const parsedSpinCoinValue = toFinitePositiveNumber(spinCoinValue)
    const candidates = new Set<number>([parsedCoinValue, parsedCoinValue / 100])

    if (parsedSpinCoinValue !== null) {
        candidates.add(parsedCoinValue / parsedSpinCoinValue)
        candidates.add(parsedCoinValue / (parsedSpinCoinValue * 100))
    }

    const finiteCandidates = Array.from(candidates).filter((candidate) => Number.isFinite(candidate) && candidate > 0)
    if (!finiteCandidates.length) {
        return undefined
    }

    for (const candidate of finiteCandidates) {
        const rounded = Math.round(candidate)
        if (Math.abs(candidate - rounded) < 1e-6 && COIN_MULTIPLIER_SET.has(rounded)) {
            return rounded
        }
    }

    const bestCandidate = pickBestCandidate(finiteCandidates)
    const nearest = nearestAllowedMultiplier(bestCandidate)
    return Math.min(HOLD_AND_WIN_MAX_MULTIPLIER, Math.max(HOLD_AND_WIN_MIN_MULTIPLIER, nearest))
}
